import type { ConsentOutput } from '@imprivata-cloud/consent';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { RootRoutes } from '@/index';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { landingPageDataTanstackCacheKey } from '../../../src/portal/containers/users/hooks/useAuthenticators';
import useAgentWindow from '../../hooks/useAgentWindow';

type FlowState = 'welcome' | 'enrollment' | 'face-enrolled';

interface RedirectError {
  code: string;
  description: string;
}

// Error sent back to the launching product when the user backs out of the flow
// without recording a decision.
const CANCEL_ERROR: RedirectError = {
  code: 'user_cancelled',
  description: 'User cancelled biometric enrollment',
};

// Error sent back to the launching product when the user explicitly declines
// the biometric consent.
const DECLINE_ERROR: RedirectError = {
  code: 'access_denied',
  description: 'User declined biometric consent',
};

interface UseEnrollFaceReturn {
  isModalOpen: boolean;
  screenState: FlowState;
  onConsentComplete: (output: ConsentOutput) => void;
  onSuccessfulFaceEnrollment: () => Promise<undefined>;
  onCancel: () => void;
  onEnrollmentDone: () => void;
  onEnrollFlowError: (err: unknown) => void;
}

export const useEnrollFace = (): UseEnrollFaceReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [screenState, setScreenState] = useState<FlowState>('welcome');

  const PRODUCT_RETURN_URL = 'productReturnUrl';
  const queryClient = useQueryClient();
  const navigateKeepSearch = useNavigateKeepSearch();
  const { endWorkflowAndCloseWindow } = useAgentWindow();
  const exitInitiatedRef = useRef(false);

  // Return to the launching product via productReturnUrl when present, otherwise
  // fall back to the self-service landing page. On cancel/decline an OIDC-style
  // error and error_description are appended so the product can react.
  const goToProductReturnUrl = useCallback(
    (error?: RedirectError) => {
      const redirectBack = new URLSearchParams(window.location.search).get(PRODUCT_RETURN_URL);

      endWorkflowAndCloseWindow();

      if (redirectBack) {
        const url = new URL(redirectBack, window.location.origin);
        if (error) {
          url.searchParams.set('error', error.code);
          url.searchParams.set('error_description', error.description);
        }
        window.location.href = url.toString();
        return;
      }

      navigateKeepSearch(RootRoutes.SELF_SERVICE);
    },
    [endWorkflowAndCloseWindow, navigateKeepSearch],
  );

  // Cancel/decline exit. Latched so the consent module firing both onComplete
  // (not_granted) and onCancel cannot trigger the redirect twice.
  const exitFlow = useCallback(
    (error: RedirectError) => {
      if (exitInitiatedRef.current) {
        return;
      }
      exitInitiatedRef.current = true;
      setIsModalOpen(false);
      goToProductReturnUrl(error);
    },
    [goToProductReturnUrl],
  );

  const onConsentComplete = useCallback(
    (output: ConsentOutput) => {
      queryClient.invalidateQueries({ queryKey: [landingPageDataTanstackCacheKey], exact: true });

      if (output.kind === 'not_granted') {
        exitFlow(DECLINE_ERROR);
        return;
      }

      if (output.kind === 'cancelled') {
        exitFlow(CANCEL_ERROR);
        return;
      }

      setScreenState('enrollment');
      setIsModalOpen(true);
    },
    [exitFlow, queryClient],
  );

  const onCancel = useCallback(() => {
    exitFlow(CANCEL_ERROR);
  }, [exitFlow]);

  const onEnrollmentDone = useCallback(() => {
    goToProductReturnUrl();
  }, [goToProductReturnUrl]);

  const onSuccessfulFaceEnrollment = useCallback(async () => {
    setIsModalOpen(false);
    setScreenState('face-enrolled');

    // Call onEnrollmentDone after 2 seconds on successful enrollment
    setTimeout(() => {
      onEnrollmentDone();
    }, 2000);
    return undefined;
  }, [onEnrollmentDone]);

  const onEnrollFlowError = useCallback((error: unknown) => {
    console.error('Error during enrollment: ', error);
  }, []);

  return {
    isModalOpen,
    screenState,
    onConsentComplete,
    onSuccessfulFaceEnrollment,
    onCancel,
    onEnrollmentDone,
    onEnrollFlowError,
  };
};
